import { useRef, useState, useEffect } from "react";
import { Play, Pause, Square, Volume2, VolumeX, Volume1, Music } from "lucide-react";
import { Slider } from "@components/common/slider";
import { cn } from "@pma/utils/helpers";

interface AudioPlayerProps {
  audio: string;
}

export default function AudioPlayer({ audio }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const formatTime = (time: number) => {
    if (time && !isNaN(time)) {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    }
    return "0:00";
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      const newTime = value[0];
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);

    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleLoadedMetadata = () => {
      if (audio) {
        setDuration(audio.duration);
        setIsLoaded(true);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (audio) {
        audio.currentTime = 0;
      }
    };

    if (audio) {
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);

      audio.volume = volume;
    }

    return () => {
      if (audio) {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", handleEnded);
      }
    };
  }, []);

  return (
    <div className='h-full md:w-[70vw] w-[60vw] flex items-center justify-center'>
      <div className='bg-white  shadow-md overflow-hidden w-full'>
        <audio ref={audioRef} src={audio} preload='metadata' />

        <div className='p-5'>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Slider
                disabled={!isLoaded}
                value={[currentTime]}
                min={0}
                max={duration || 100}
                step={0.1}
                onValueChange={handleSeek}
                className={cn("cursor-pointer ", !isLoaded && "opacity-50")}
              />
              <div className='flex justify-between text-xs text-gray-500'>
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-2'>
                <button
                  onClick={togglePlay}
                  className='h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white hover:bg-primary/80 transition-colors'
                  disabled={!isLoaded}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} className='ml-0.5' />}
                </button>

                <button
                  onClick={handleStop}
                  className='h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300 transition-colors'
                  disabled={!isLoaded || currentTime === 0}
                >
                  <Square size={14} />
                </button>
              </div>

              <div className='flex items-center space-x-2 w-28'>
                <button onClick={toggleMute} className='text-gray-500 hover:text-gray-700'>
                  {isMuted ? (
                    <VolumeX size={18} />
                  ) : volume > 0.5 ? (
                    <Volume2 size={18} />
                  ) : (
                    <Volume1 size={18} />
                  )}
                </button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  min={0}
                  max={1}
                  step={0.01}
                  onValueChange={handleVolumeChange}
                  className='cursor-pointer'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
